import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuthContext } from "../services/AuthContext";
import api from "../services/api";
import { 
  Container, 
  Header, 
  Logo, 
  Nav, 
  Hamburger,
  MobileMenuOverlay,
  MobileMenu,
  GrabberHandle,
  MobileMenuLogo,
  Divider,
  MenuLink,
  Main, 
  LoginBox, 
  Title, 
  FormGrid,
  InputField, 
  OTPContainer,
  OTPInput,
  LoginButton, 
  LinkText, 
  Footer,
  ErrorMessage
} from "../styles/SignupStyles";
import bgImage from '../assets/bgimagefinal.png';
import logo from '../assets/logo-white.png';

function Signup() {
  const { login } = useAuthContext();
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    appointment: "",
    company_name: "",
    address: "",
    market_segment: "",
    country: "",
    contact_no: "",
    user_type: "",
    otp: ["", "", "", ""],
  });
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [timeLeft, setTimeLeft] = useState(60);
  const [expiry, setExpiry] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef(null);

  useEffect(() => {
    console.log("Signup component rendered");
  }, []);

  useEffect(() => {
    if (isOtpSent && expiry) {
      const timer = setInterval(() => {
        const now = Date.now() / 1000;
        const remaining = Math.max(0, Math.floor(expiry - now));
        setTimeLeft(remaining);
        if (remaining === 0) {
          setIsOtpSent(false);
          setFormData((prev) => ({ ...prev, otp: ["", "", "", ""] }));
          setError("OTP expired. Please request a new OTP.");
          setExpiry(null);
        }
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [isOtpSent, expiry]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  const handleOtpChange = (index) => (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "").slice(0, 1);
    const newOtp = [...formData.otp];
    newOtp[index] = value;
    setFormData((prev) => ({ ...prev, otp: newOtp }));
    setError("");

    if (value && index < 3) {
      document.getElementsByName(`otp-${index + 1}`)[0].focus();
    }
  };

  const handleSignup = async () => {
    setIsLoading(true);
    setError("");
    if (!isOtpSent) {
      const requiredFields = ['username', 'email', 'password', 'user_type'];
      const missingFields = requiredFields.filter(field => !formData[field]);
      if (missingFields.length > 0) {
        setError(`Please fill in all required fields: ${missingFields.join(', ')}`);
        setIsLoading(false);
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        setError("Please enter a valid email address");
        setIsLoading(false);
        return;
      }
      if (formData.username.length < 3) {
        setError("Username must be at least 3 characters long");
        setIsLoading(false);
        return;
      }
      if (formData.password.length < 6) {
        setError("Password must be at least 6 characters long");
        setIsLoading(false);
        return;
      }
      if (formData.contact_no && !/^\+?\d{10,15}$/.test(formData.contact_no)) {
        setError("Invalid contact number format");
        setIsLoading(false);
        return;
      }
      try {
        console.log("Sending signup data:", formData);
        const response = await api.post("/auth/signup", {
          username: formData.username,
          email: formData.email,
          password: formData.password,
          appointment: formData.appointment,
          company_name: formData.company_name,
          address: formData.address,
          market_segment: formData.market_segment,
          country: formData.country,
          contact_no: formData.contact_no,
          user_type: formData.user_type || "Buyer",
        });
        console.log("Signup response:", response.data);
        if (!response.data.expiry) {
          setError("Invalid response from server: OTP expiry not provided");
          setIsLoading(false);
          return;
        }
        setIsOtpSent(true);
        setExpiry(response.data.expiry);
        setTimeLeft(60);
        alert(`An OTP has been sent to ${formData.email}`);
      } catch (error) {
        console.error("Signup Error:", error.response?.data || error.message);
        let errorMessage = error.response?.data?.detail || "Failed to initiate signup!";
        if (error.response?.status === 500) {
          errorMessage = "Server error: Unable to send OTP. Please try again later or contact support.";
        }
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    } else {
      const otp = formData.otp.join("");
      if (otp.length !== 4 || !/^\d{4}$/.test(otp)) {
        setError("Please enter a valid 4-digit OTP");
        setIsLoading(false);
        return;
      }
      try {
        console.log("Verifying OTP:", otp);
        const response = await api.post("/auth/verify-otp", { email: formData.email, otp });
        console.log("OTP verification response:", response.data);
        alert("Signup successful! Please log in to continue.");
        navigate("/login");
      } catch (err) {
        console.error("OTP Verification Error:", err.response?.data || err.message);
        setError(err.response?.data?.detail || "Invalid OTP!");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleResendOtp = async () => {
    setIsLoading(true);
    setIsOtpSent(false);
    setFormData((prev) => ({ ...prev, otp: ["", "", "", ""] }));
    setError("");
    setTimeLeft(60);
    setExpiry(null);
    await handleSignup();
  };

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        closeMenu();
      }
    };

    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen]);

  const handleLinkClick = (e, path) => {
    const link = e.currentTarget;
    const ripple = document.createElement("span");
    ripple.classList.add("ripple");
    const rect = link.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    ripple.style.width = ripple.style.height = `${size}px`;
    ripple.style.left = `${e.clientX - rect.left - size / 2}px`;
    ripple.style.top = `${e.clientY - rect.top - size / 2}px`;
    link.appendChild(ripple);

    if ("vibrate" in navigator) {
      navigator.vibrate(50);
    }

    setTimeout(() => {
      closeMenu();
      navigate(path);
    }, 200);
  };

  const getLinkClass = (path) => {
    return location.pathname === path ? "active" : "";
  };

  const menuItems = [
    { label: "HOME", to: "/" },
    { label: "ABOUT", to: "/about" },
    { label: "CONTACT", to: "/contact" },
    { label: "LOGIN", to: "/login" },
  ];

  const inputFields = [
    { name: "username", placeholder: "John", type: "text" },
    { name: "email", placeholder: "johndoe@gmail.com", type: "email" },
    { name: "password", placeholder: "Password", type: "password" },
    { name: "appointment", placeholder: "Position", type: "text" },
    { name: "company_name", placeholder: "Company Name", type: "text" },
    { name: "address", placeholder: "Address", type: "text" },
    { name: "market_segment", placeholder: "Market Segment", type: "text" },
    { name: "country", placeholder: "Country", type: "text" },
    { name: "contact_no", placeholder: "Contact Number", type: "text" },
  ];

  return (
    <Container bgImage={bgImage}>
      <Header>
        <Logo src={logo} alt="Nutrify Today Logo" onError={() => console.error("Logo failed to load")} />
        <Nav>
          {menuItems.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              className={`menu-item ${getLinkClass(item.to)}`}
            >
              {item.label}
            </Link>
          ))}
          <Link to="/signup" className={`menu-item register ${getLinkClass("/signup")}`}>
            REGISTER
          </Link>
        </Nav>
        <Hamburger 
          className={menuOpen ? 'open' : ''} 
          onClick={toggleMenu}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && toggleMenu()}
        >
          <span></span>
          <span></span>
          <span></span>
        </Hamburger>
      </Header>
      <MobileMenuOverlay isOpen={menuOpen} onClick={closeMenu} />
      <MobileMenu isOpen={menuOpen} ref={menuRef}>
        <MobileMenuLogo src={logo} alt="Nutrify Today Logo" onError={() => console.error("Mobile menu logo failed to load")} />
        <Divider />
        {menuItems.map((item) => (
          <MenuLink 
            as={Link}
            key={item.label}
            to={item.to} 
            className={getLinkClass(item.to)} 
            onClick={(e) => handleLinkClick(e, item.to)} 
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && handleLinkClick(e, item.to)}
          >
            {item.label}
          </MenuLink>
        ))}
        <MenuLink 
          as={Link}
          to="/signup" 
          className={`register ${getLinkClass("/signup")}`} 
          onClick={(e) => handleLinkClick(e, "/signup")} 
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && handleLinkClick(e, "/signup")}
        >
          REGISTER
        </MenuLink>
        <GrabberHandle 
          onClick={closeMenu}
          aria-label="Close menu"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && closeMenu()}
        />
      </MobileMenu>
      <Main>
        <LoginBox>
          <Title>Sign Up</Title>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <FormGrid>
            {inputFields.map((field, index) => (
              <InputField
                key={index}
                type={field.type}
                name={field.name}
                placeholder={field.placeholder}
                value={formData[field.name]}
                onChange={handleChange}
                required={field.name === "username" || field.name === "email" || field.name === "password"}
                disabled={isOtpSent || isLoading}
              />
            ))}
            <select
              name="user_type"
              value={formData.user_type}
              onChange={handleChange}
              required
              disabled={isOtpSent || isLoading}
              style={{ width: "92%", padding: "0.75rem", margin: "0.5rem 0", border: "1px solid #ccc", borderRadius: "20px", fontSize: "1rem" }}
            >
              <option value="">Select User Type</option>
              <option value="Buyer">Buyer</option>
              <option value="Supplier">Supplier</option>
              <option value="ContractManufacturer">Contract Manufacturer</option>
              <option value="Others">Others</option>
            </select>
          </FormGrid>
          {!isOtpSent ? (
            <LoginButton 
              onClick={handleSignup}
              disabled={isLoading}
            >
              {isLoading ? "Sending OTP..." : "SEND OTP"}
            </LoginButton>
          ) : (
            <>
              <p style={{ fontSize: "0.9rem", color: "#666", margin: "0.5rem 0", gridColumn: "span 2", textAlign: "center" }}>
                An OTP has been sent to {formData.email}. This code will expire in {timeLeft} seconds.
              </p>
              <OTPContainer>
                {formData.otp.map((digit, index) => (
                  <OTPInput
                    key={index}
                    type="text"
                    name={`otp-${index}`}
                    value={digit}
                    onChange={handleOtpChange(index)}
                    maxLength="1"
                    required
                    disabled={isLoading}
                  />
                ))}
              </OTPContainer>
              <LoginButton 
                onClick={handleSignup}
                disabled={isLoading}
              >
                {isLoading ? "Verifying OTP..." : "VERIFY OTP"}
              </LoginButton>
              {timeLeft === 0 && (
                <LoginButton 
                  onClick={handleResendOtp} 
                  style={{ backgroundColor: "#666", marginTop: "0.5rem" }}
                  disabled={isLoading}
                >
                  RESEND OTP
                </LoginButton>
              )}
            </>
          )}
          <LinkText>
            Already have an account? <a href="/login">Login</a>
          </LinkText>
        </LoginBox>
      </Main>
      <Footer>
        <p>© 2025 DealSphere. All rights reserved.</p>
        <div>
          <a href="#terms">Terms</a>
          <a href="#privacy">Privacy</a>
          <a href="#contact">Contact</a>
        </div>
      </Footer>
    </Container>
  );
}

export default Signup;